import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { IDockviewPanelProps } from "dockview-react";
import { api } from "./utils/api";
import { isValidDateString } from "./utils/utils";
import ReactQuill from "react-quill-new";


let modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image"],
  ],
};

let formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "indent",
  "link",
  "image",
];

interface ICalendarDay {
  date: Date;
  events: ICalendarEvent[];
}

interface ICalendarEvent {
  id: number;
  title: string;
  tickers: string;
  description: string; // quill content
}

export interface IEditingEvent {
  id: number;
  title: string;
  tickers: string;
  date: string;
  description: string;
}

interface ISearchParams {
  startDate: string;
  page: string;
}

  export function daysRemaining(date: Date) {
    // calculate the remaining days from today to date
    const today = new Date();
    const timeDiff = date.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }


export const Calendar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [events, setEvents] = useState<ICalendarDay[]>([]);
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);
  const [editingEvent, setEditingEvent] = useState<IEditingEvent | null>(null);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [searchParams, setSearchParams] = useState<ISearchParams>({
    // start date yesterday in yyyy-mm-dd
    startDate: new Date(Date.now() - 86400000).toISOString().split("T")[0],
    page: "1",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    search(new Event("init") as unknown as React.FormEvent);
  }, []);


  function toggleEventDescription(eventId: number) {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  }

  function editEvent(event: ICalendarEvent, day: ICalendarDay) {
    // load event
    setEditingEvent({
      id: event.id,
      title: event.title,
      tickers: event.tickers,
      date: day.date.toISOString().split("T")[0],
      description: event.description,
    });
  }

  function submitEvent() {
    // validate input
    if (!editingEvent) {
      setError("No event loaded for editing");
      return;
    }

    // title required
    if (editingEvent.title.trim() === "") {
      setError("Title is required");
      return;
    }

    // date valid day (like 2024-06-30)
    if (!isValidDateString(editingEvent.date)) {
      setError("Date must be in YYYY-MM-DD format");
      return;
    }

    // vaidate tickers (alphanumeric and spaces only)
    const tickerPattern = /^[a-zA-Z0-9 ]*$/;
    if (!tickerPattern.test(editingEvent.tickers)) {
      setError("Tickers can only contain alphanumeric characters and spaces");
      return;
    }

    setError("");
    setSuccessMessage('')
    setIsLoading(true); 

    api.post("/calendar-events", {
      id: editingEvent.id,
      title: editingEvent.title,
      tickers: editingEvent.tickers,
      date: editingEvent.date,
      description: editingEvent.description,
    })
    .then((id) => {
      setSuccessMessage("Event submitted successfully");
      search(new Event("submit") as unknown as React.FormEvent);
      setEditingEvent(null);
    })
    .catch((error) => {
      console.error(error);
      setError("Error submitting event");
    }).finally(() => {
      setIsLoading(false);
    });
  }



  function search(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    api.get("/calendar-events", {
      params: {
        start_date: searchParams.startDate,
        page: searchParams.page,
      },
    })
    .then((response) => {
      // map response data to events
      const data: ICalendarDay[] = response.data.map((day: any) => ({
        date: new Date(day.date),
        events: day.events.map((event: any) => ({
          id: event.id,
          title: event.title,
          tickers: event.tickers,
          description: event.description,
        })),
      }));

      setEvents(data);
    })
    .catch((error) => {
      setError("Error fetching events");
    }).finally(() => {
      setIsLoading(false);
    });
  }

  function addNew() {
    if (editingEvent?.id === -1) {
      setEditingEvent(null);
      return;
    }

    // load event with id -1
    setEditingEvent({
      id: -1,
      title: "",
      tickers: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
    });
  }

  function deleteEvent(eventId: number) {
    setIsLoading(true); 
    setError("");
    setSuccessMessage("");

    api.delete(`/calendar-events/${eventId}`)
    .then(() => {
      setSuccessMessage("Event deleted successfully");
      // remove event from state
    
      setEvents((prevEvents) =>
        prevEvents.map((day) => ({
          ...day,
          events: day.events.filter((event) => event.id !== eventId),
        }))
      );
    })
    .catch((error) => {
      setError("Error deleting event");
    }).finally(() => { 
      setIsLoading(false);
    })
  }

  if (isLoading) { 
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-4 h-full overflow-auto w-full">
      {location.pathname.startsWith("/mobile") && (
        <Link to="/mobile" className="btn btn-default mb-2">To Menu</Link>
      )}
      {error && <div className="text-red-500 mb-2">{error}</div>}
      {successMessage && (
        <div className="text-green-500 mb-2">{successMessage}</div>
      )}

      <div className="flex gap-4">

      {/* Sarchbox */}
      <form onSubmit={search} className="flex gap-4 mb-8">
        <div className="flex-grow">
          <input type="text" placeholder="Start YYYY-MM-DD" className="" onChange={(e) => setSearchParams((prev) => ({ ...prev, startDate: e.target.value }))} value={searchParams.startDate} />
        </div>

        <div className="w-36">
          <input type="text" placeholder="Page" className="" onChange={(e) => setSearchParams((prev) => ({ ...prev, page: e.target.value }))} value={searchParams.page} />
        </div>

        <div>
          <button type="submit" className="btn btn-default">
            Search
          </button>
        </div>

      </form>

        <div>
          <button onClick={addNew} className="btn btn-default">
            Add new
          </button>
        </div>

        </div>
      {/* If editing event id is -1 => show add event form */}
      {editingEvent && editingEvent.id === -1 && (
        <div className="ml-8">
          <EditOrAddEvent
            editingEvent={editingEvent}
            setEditingEvent={setEditingEvent}
            submitEvent={submitEvent}
          />
        </div>
      )}

      {events.map((day) => (
        <div key={day.date.toISOString()} className="mb-14">
          <p className="text-sm text-gray-600">
            {day.date.toDateString()} <span>({daysRemaining(day.date)}d)</span>
          </p>

          {day.events.map((event) => (
            <div className="pl-8" key={event.id}>
              {/* Reading-only View */}
              <div
                className={`mb-4 space-y-1 ${
                  editingEvent && editingEvent.id === event.id ? "hidden" : ""
                }`}
              >
                <strong className="">
                  {event.title} {" "}
                  {event.tickers !== "" && <span className="ner-loc">({event.tickers})</span>}
                </strong>
                <div className="space-x-8">
                  <a
                    className="text-gray-600"
                    onClick={() => toggleEventDescription(event.id)}
                  >
                    &gt; Description
                  </a>

                  <a
                    className="text-gray-600"
                    onClick={() => editEvent(event, day)}
                  >
                    &gt; Edit
                  </a>
        
                  <a
                    className="text-red-600"
                    onClick={() => deleteEvent(event.id)}
                  >
                    &gt; Delete
                  </a>
                </div>
                <div
                  className={expandedEventId === event.id ? "block" : "hidden"}
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>

              {/* Edit View */}
              {editingEvent && editingEvent.id === event.id && (
                <div className="ml-8">
                  <EditOrAddEvent
                    editingEvent={editingEvent}
                    setEditingEvent={setEditingEvent}
                    submitEvent={submitEvent}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const EditOrAddEvent = ({
  editingEvent,
  setEditingEvent,
  submitEvent,
}: {
  editingEvent: IEditingEvent;
  setEditingEvent: React.Dispatch<React.SetStateAction<IEditingEvent | null>>;
  submitEvent: () => void;
}) => {
  return (
    <div>
      <div className="flex gap-4 mb-2">
        {/* Title */}
        <input
          placeholder="Title"
          id="title"
          className="block"
          type="text"
          value={editingEvent.title}
          onChange={(e) => {
            const newTitle = e.target.value;
            setEditingEvent((prev) =>
              prev
                ? {
                    ...prev,
                    title: newTitle,
                  }
                : prev
            );
          }}
        />

        {/* Tickers */}
        <div className="w-64">
          <input
            placeholder="AAPL GOOG"
            id="tickers"
            className=""
            type="text"
            value={editingEvent.tickers}
            onChange={(e) => {
              const newTickers = e.target.value;
              setEditingEvent((prev) =>
                prev
                  ? {
                      ...prev,
                      tickers: newTickers,
                    }
                  : prev
              );
            }}
          />
        </div>

        {/* Date*/}
        <input
          placeholder="YYYY-MM-DD"
          id="date"
          className="block w-32"
          type="text"
          value={editingEvent.date}
          onChange={(e) => {
            const newDate = e.target.value;
            setEditingEvent((prev) =>
              prev
                ? {
                    ...prev,
                    date: newDate,
                  }
                : prev
            );
          }}
        />

        <button onClick={submitEvent} className="btn btn-default">
          Submit
        </button>
      </div>

      {/* Description */}
      <div className="h-[200px] mb-12">
        <ReactQuill
          className="h-full"
          theme="snow"
          value={editingEvent.description}
          modules={modules}
          formats={formats}
          onChange={(content) => {
            setEditingEvent((prev) =>
              prev
                ? {
                    ...prev,
                    description: content,
                  }
                : prev
            );
          }}
        />
      </div>
    </div>
  );
};
